import { useState } from "react";
import { Alert, Autocomplete, Box, Button, Card, CardContent, Divider, Grid, LinearProgress, Paper, Stack, TextField, Typography, Chip, InputAdornment, } from "@mui/material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import MenuFrame from "../frames/menu_frame";
import DropDownLoading from "../frames/dropdown_loading";
import { getAccountType, newAccount } from "./accounts_halper";

function NewAccount() {
    const [inputErrors, setInputErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [accType, setAccType] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);

    const initData = async (auth) => {
        if (!auth) return;
        setAccType(await getAccountType());
    };

    const __error_handler = (test, vr, msg) => {
        if (test) {
            inputErrors[vr] = "";
            setInputErrors({ ...inputErrors });
        } else {
            inputErrors[vr] = msg;
            setInputErrors({ ...inputErrors });
        }
        return !test;
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (isLoading) return;
        const data = new FormData(event.currentTarget);
        const name = data.get("name");
        const ac_type = data.get("ac_type");
        let flag = true;
        if (__error_handler(name.length > 3, "name", "Please enter valid name.")) {
            flag = false;
        }
        if (__error_handler(ac_type.length > 0, "ac_type", "Please enter valid account type.")) {
            flag = false;
        }

        if (flag) {
            const _json = { name: name, type: ac_type };
            setIsLoading(true);
            newAccount(_json).then(() => {
                setAlertMessage({ severity: "success", message: "New account created." });
                setIsLoading(false);
                event.target.reset();
            }).catch((err) => {
                setAlertMessage({ severity: "error", message: err.message });
                setIsLoading(false);
            });
        }
    };

    const validateInputs = () => {
        return true;
    };

    return (
        <MenuFrame permission="ORG_ADMIN" onInit={initData} screen="NEW_ACCOUNT">
            <Box sx={{ maxWidth: 950, mx: "auto", py: 4, }} >
                <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid", borderColor: "divider", overflow: "hidden", }} >
                    <Box sx={{ px: 4, py: 3, background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)", color: "white" }}>
                        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                            <Box sx={{ width: 56, height: 56, borderRadius: 3, bgcolor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", }} >
                                <AddCircleOutlineRoundedIcon fontSize="large" />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={700} >
                                    Create New Account
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }} >
                                    Add a new ledger/account to your accounting system.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {isLoading && <LinearProgress />}

                    <CardContent sx={{ p: 4 }}>
                        <Box component="form" noValidate onSubmit={handleSubmit} >
                            {alertMessage && (
                                <Alert severity={alertMessage.severity} sx={{ mb: 3, borderRadius: 2 }}>
                                    {alertMessage.message}
                                </Alert>
                            )}

                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        name="name"
                                        label="Account Name"
                                        placeholder="Enter account name"
                                        error={!!inputErrors.name}
                                        helperText={inputErrors.name}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountBalanceWalletRoundedIcon color="primary" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Autocomplete
                                        disablePortal
                                        options={accType ?? []}
                                        noOptionsText={<DropDownLoading title="Loading Account Types..." />}
                                        getOptionLabel={(option) => option.name}
                                        renderOption={(props, option) => {
                                            const { key, ...optionProps } = props;
                                            return (
                                                <Box
                                                    key={key}
                                                    component="li"
                                                    {...optionProps}
                                                    sx={{ py: 1, }}
                                                >
                                                    <Stack width="100%" direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }} >
                                                        <Typography fontWeight={500}>
                                                            {option.name}
                                                        </Typography>

                                                        <Chip size="small" color="primary" label={option.scheme} />
                                                    </Stack>
                                                </Box>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                name="ac_type"
                                                label="Account Type"
                                                placeholder="Select account type"
                                                error={!!inputErrors.ac_type}
                                                helperText={inputErrors.ac_type}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start">
                                                                <CategoryRoundedIcon color="primary" />
                                                            </InputAdornment>
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid size={12}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>

                                <Grid size={12}>
                                    <Button fullWidth size="large" type="submit" variant="contained" color="primary" onClick={validateInputs} startIcon={<AddCircleOutlineRoundedIcon />} disabled={isLoading} sx={{ py: 1.5, borderRadius: 2, fontWeight: 700, textTransform: "none", fontSize: 16, }}>
                                        Create Account
                                    </Button>
                                </Grid>

                                <Grid size={12}>
                                    <Paper variant="outlined" sx={{ mt: 2, p: 3, borderRadius: 3, bgcolor: "grey.50", }}>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 2 }}>
                                            <InfoOutlinedIcon color="primary" />

                                            <Typography variant="h6" fontWeight={700} >
                                                Accounting Reference
                                            </Typography>
                                        </Stack>

                                        <Stack spacing={1}>
                                            <Typography variant="body2">
                                                <strong>Debit (Dr):</strong>{" "} Increases assets or expenses and decreases liabilities, equity, revenue or income.
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                <strong>Debit (Dr):</strong>{" "} परिसंपत्तियों या व्ययों में वृद्धि होती है तथा देनदारियों, इक्विटी, आय में कमी होती है।
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Credit (Cr):</strong>{" "} Increases liabilities, equity, income or revenue and decreases assets or expenses.
                                            </Typography>

                                            <Typography variant="body2" color="textSecondary">
                                                <strong>Credit (Cr):</strong>{" "} देनदारियों, इक्विटी या आय में वृद्धि होती है तथा परिसंपत्तियों या व्यय में कमी होती है।
                                            </Typography>
                                        </Stack>

                                        <Divider sx={{ my: 3 }} />

                                        <Typography variant="subtitle1" fontWeight={700} gutterBottom >
                                            Account Types & Debit/Credit Effect
                                        </Typography>

                                        <Stack spacing={1} sx={{ mt: 2 }} >
                                            <Chip variant="outlined" color="success" label="Assets → Debit (+) | Credit (-)" />

                                            <Chip variant="outlined" color="warning" label="Liabilities → Debit (-) | Credit (+)" />

                                            <Chip variant="outlined" color="secondary" label="Equity → Debit (-) | Credit (+)" />

                                            <Chip variant="outlined" color="info" label="Revenue → Debit (-) | Credit (+)" />

                                            <Chip variant="outlined" color="primary" label="Expenses → Debit (+) | Credit (-)" />
                                        </Stack>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </MenuFrame>
    );
}

export default NewAccount;
